/**
 * Learner level from syllabus completion % - not user-selected.
 * Thresholds shift as syllabus grows; completion % can drop when new material is added.
 */

import { JYOTISHA_MARGA_TAB_GROUPS } from '../data/jyotishaMargaTabGroups';

const SYLLABUS_TOPIC_IDS = JYOTISHA_MARGA_TAB_GROUPS.flatMap((g) => g.tabs.map((t) => t.id));

/** @typedef {'starter' | 'beginner' | 'intermediate' | 'advanced'} StudentLevelId */

export const LEVEL_THRESHOLDS = {
  beginner: 20,
  intermediate: 50,
  advanced: 80,
};

export const LEVEL_META = {
  starter: { hi: 'प्रतिबद्ध', en: 'Committer', order: 0 },
  beginner: { hi: 'शुरुआती', en: 'Beginner', order: 1 },
  intermediate: { hi: 'मध्यम', en: 'Intermediate', order: 2 },
  advanced: { hi: 'उन्नत', en: 'Advanced', order: 3 },
};

/** Shield images under `public/assets/user-type/` */
export const LEVEL_SHIELDS = {
  starter: '/schools/parashari/assets/user-type/committer.png',
  beginner: '/schools/parashari/assets/user-type/beginner.png',
  intermediate: '/schools/parashari/assets/user-type/intermediate.png',
  advanced: '/schools/parashari/assets/user-type/advanced.png',
};

/** All learner tiers shown on profile (Committer → Advanced). */
export const SHIELD_LEVELS = ['starter', 'beginner', 'intermediate', 'advanced'];

/** Shown on home/profile shield UI - syllabus grows over time. */
export const SHIELD_KEEP_LEARNING_COPY = {
  en: 'New lessons land every week. Keep going to hold your shield level.',
  hi: 'हर हफ़्ते नए पाठ जुड़ते हैं। ढाल बनाए रखने के लिए पढ़ते रहें।',
};

const LEVEL_ORDER = SHIELD_LEVELS;

/**
 * @param {number} pct 0–100 syllabus completion
 * @returns {StudentLevelId}
 */
export function computeLevelFromCompletionPct(pct) {
  const n = Math.max(0, Math.min(100, Math.round(pct)));
  if (n >= LEVEL_THRESHOLDS.advanced) return 'advanced';
  if (n >= LEVEL_THRESHOLDS.intermediate) return 'intermediate';
  if (n >= LEVEL_THRESHOLDS.beginner) return 'beginner';
  return 'starter';
}

/**
 * Daily study minutes tier - starter uses beginner baseline.
 * @param {StudentLevelId} level
 */
export function dailyMinutesForLevel(level) {
  const map = { starter: 15, beginner: 15, intermediate: 25, advanced: 35 };
  return map[level] ?? 15;
}

/**
 * @param {{ completed: number; total: number; pct: number }} progressSummary
 * @param {boolean} [showEnglish]
 */
export function getStudentLevelDetails(progressSummary, showEnglish = true) {
  const pct = progressSummary?.pct ?? 0;
  const total = progressSummary?.total ?? 0;
  const completed = progressSummary?.completed ?? 0;
  const level = computeLevelFromCompletionPct(pct);

  const levelIdx = LEVEL_ORDER.indexOf(level);
  const nextLevel = levelIdx < LEVEL_ORDER.length - 1 ? LEVEL_ORDER[levelIdx + 1] : null;

  const thresholdForNext =
    nextLevel === 'beginner'
      ? LEVEL_THRESHOLDS.beginner
      : nextLevel === 'intermediate'
        ? LEVEL_THRESHOLDS.intermediate
        : nextLevel === 'advanced'
          ? LEVEL_THRESHOLDS.advanced
          : 100;

  const pctToNext = nextLevel ? Math.max(0, thresholdForNext - pct) : 0;
  const targetCompletedForNext = nextLevel ? Math.ceil((thresholdForNext / 100) * total) : total;
  const lessonsNeeded = nextLevel ? Math.max(0, targetCompletedForNext - completed) : 0;

  const label = (id) => {
    const m = LEVEL_META[id];
    return showEnglish ? m.en : m.hi;
  };

  return {
    level,
    levelLabel: label(level),
    nextLevel,
    nextLevelLabel: nextLevel ? label(nextLevel) : null,
    completionPct: pct,
    totalLessons: total,
    completedLessons: completed,
    pctToNext,
    lessonsNeeded,
    nextThreshold: thresholdForNext,
    thresholds: LEVEL_THRESHOLDS,
    isMaxLevel: level === 'advanced',
  };
}

/**
 * Sync profile.studentLevel + dailyGoalMinutes from syllabus progress.
 * @param {import('./studentProfileStorage').StudentProfileState} state
 */
export function applyComputedStudentLevel(state) {
  const completed = SYLLABUS_TOPIC_IDS.filter((id) => state.topicProgress[id]?.status === 'completed').length;
  const total = SYLLABUS_TOPIC_IDS.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const level = computeLevelFromCompletionPct(pct);
  const dailyGoalMinutes = dailyMinutesForLevel(level);

  if (state.profile.studentLevel === level && state.profile.dailyGoalMinutes === dailyGoalMinutes) {
    return state;
  }

  return {
    ...state,
    profile: {
      ...state.profile,
      studentLevel: level,
      dailyGoalMinutes,
    },
  };
}

/** @param {import('./studentProfileStorage').StudentProfileState} state */
export function getSyllabusCompletionFromState(state) {
  const completed = SYLLABUS_TOPIC_IDS.filter((id) => state.topicProgress[id]?.status === 'completed').length;
  const total = SYLLABUS_TOPIC_IDS.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { completed, total, pct };
}
